import { useState, useEffect, Suspense, lazy } from "react";
import { Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import ProtectedRoute from "./routes/ProtectedRoute";
import PublicRoute from "./routes/PublicRoute";
import RoleRoute from "./routes/RoleRoute";
import MobileWarningOverlay from "./components/common/MobileOverlay";
import DashboardLayout from "./components/layout/DashboardLayout";
import NotFoundPage from "./pages/NotFound";
import Loader from "./components/common/Loader";

const MOBILE_BREAKPOINT = 768;

// Lazy-loaded pages
const Login = lazy(() => import("./pages/Auth/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const SchooList = lazy(() => import("./pages/Schools/SchoolList"));
const SchoolDetailPage = lazy(() => import("./pages/Schools/SchoolView"));

const StudentList = lazy(() => import("./pages/Students/StudentList"));
const StudentDetailPage = lazy(() => import("./pages/Students/StudentView"));

const TeachersList = lazy(() => import("./pages/Teachers/TeachersList"));
const TeacherDetailPage = lazy(() => import("./pages/Teachers/TeachersView"));
const AdminList = lazy(() => import("./pages/Admin/AdminList"));
const Users = lazy(() => import("./pages/Users/Users.jsx"));

const BannersList = lazy(() => import("./pages/Banners/BannersList"));
const WeeklyTimetable = lazy(() => import("./pages/Timetable/WeeklyTimetable"));
const Holidays = lazy(() => import("./pages/Holidays/Holidays"));
const SchoolEvents = lazy(() => import("./pages/Events/Events"));
const SubjectList = lazy(() => import("./pages/Subjects/SubjectList"));
const MasterBoardsList = lazy(() => import("./pages/Master/Boards/BoardsList"));
const MasterClassesList = lazy(() => import("./pages/Master/Classes/ClassesList"));
const MasterSubjectsList = lazy(() => import("./pages/Master/Subjects/SubjectsList"));
const MasterLessonsList = lazy(() => import("./pages/Master/Lessons/LessonsList"));
const MasterMaterialList = lazy(() => import("./pages/Master/Material/MaterialList"));
const MasterTestDetailPage = lazy(() => import("./pages/Master/Material/TestDetailPage"));
const ExamsList = lazy(() => import("./pages/Exams/ExamsList"));
const ExamDetailPage = lazy(() => import("./pages/Exams/ExamDetail"));
const QuizList = lazy(() => import("./pages/Quiz/QuizList"));
const QuizDetailPage = lazy(() => import("./pages/Quiz/QuizDetailPage"));
const QuizResultsPage = lazy(() => import("./pages/Quiz/QuizResultsPage"));
const QuizStatsPage = lazy(() => import("./pages/Quiz/QuizStatsPage"));
const FeeComponents = lazy(() => import("./pages/Students/FeeComponents"));
const FeeStructure = lazy(() => import("./pages/Fee/FeeStructure.jsx"));
const LibraryList = lazy(() => import("./pages/Library/LibraryList"));
const DoubtsList = lazy(() => import("./pages/Doubts/DoubtsList"));
const StudentFees = lazy(() => import("./pages/Fee/StudentFees.jsx"));

const App = () => {
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_BREAKPOINT,
  );

  // Efficient resize handling using matchMedia
  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);

    const handleChange = (e) => {
      setIsMobile(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    setIsMobile(mediaQuery.matches);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  if (isMobile) return <MobileWarningOverlay />;

  return (
    <>
      <Toaster position="top-center" toastOptions={{ duration: 1500 }} />

      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Public Route */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />

            <Route path="schools" element={<SchooList />} />
            <Route
              path="schools/detail/:sc_id"
              element={<SchoolDetailPage />}
            />

            <Route path="students" element={<StudentList />} />
            <Route
              path="students/detail/:st_id"
              element={<StudentDetailPage />}
            />

            <Route path="teachers" element={<TeachersList />} />
            <Route
              path="teachers/detail/:teacher_id"
              element={<TeacherDetailPage />}
            />
            <Route path="banners" element={<BannersList />} />
            <Route path="timetable" element={<WeeklyTimetable />} />
            <Route path="holidays" element={<Holidays />} />
            <Route path="events" element={<SchoolEvents />} />
            <Route path="subjects" element={<SubjectList />} />
            <Route
              path="master"
              element={
                <RoleRoute roles={["super_admin"]}>
                  <Outlet />
                </RoleRoute>
              }
            >
              <Route path="boards" element={<MasterBoardsList />} />
              <Route path="board-classes" element={<MasterClassesList />} />
              <Route path="subjects" element={<MasterSubjectsList />} />
              <Route path="lessons" element={<MasterLessonsList />} />
              <Route path="material/test/:m_id" element={<MasterTestDetailPage />} />
              <Route path="material/:l_id/:bc_id/:type" element={<MasterMaterialList />} />
            </Route>
            <Route path="exams" element={<ExamsList />} />
            <Route
              path="/exams/detail/:exam_code"
              element={<ExamDetailPage />}
            />
            <Route path="quiz" element={<QuizList />} />
            <Route path="quiz/detail/:sq_id" element={<QuizDetailPage />} />
            <Route path="quiz/results/:sq_id" element={<QuizResultsPage />} />
            <Route path="quiz/stats/:sq_id" element={<QuizStatsPage />} />
            <Route path="fee-components" element={<FeeComponents />} />
            <Route path="fee-structure" element={<FeeStructure />} />
            <Route path="student-fees" element={<StudentFees />} />
            <Route path="library" element={<LibraryList />} />
            <Route path="doubts" element={<DoubtsList />} />
            <Route path="users" element={<Users />} />
            <Route path="admins" element={<AdminList />} />
          </Route>

          {/* Not Found */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </>
  );
};

export default App;
